import { GridRenderCellParams } from '@mui/x-data-grid-premium'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { CustomRenderColumnConfig } from '@omnea-app/fe-tables/columns/definitions/getCustomRenderColumn'
import { ColumnConfig } from '@omnea-app/fe-tables/columns/useColumnConfig'
import { RowGroupingColumnTableCell } from '@omnea-app/fe-tables/row-grouping/components/RowGroupingColumnTableCell'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RiskAggregationEntity, SummaryRiskLevel } from '@omnea-app/risks/risksFrontendClient'
import { RiskMethodologyCalculatorFactory } from '../edit/shared/form/sections/risk-level/chips/content/data/RiskMethodologyCalculatorFactory'
import { RiskMatrixScoreChip } from '../edit/shared/form/sections/risk-level/chips/RiskMatrixScoreChip'
import { useGetMaxRiskScoresInList } from '../score/useGetMaxRiskScoresInList'
import { useGetRiskSettings } from '../settings/hooks/useGetRiskSettings'

type SupportedRow = {
  risks: SummaryRiskLevel[]
  inherentRisk: number | null
  residualRisk: number | null
}

type Input<T extends SupportedRow> = {
  getViewRisksUrl: (params: GridRenderCellParams<T>) => string
  aggregationEntity: RiskAggregationEntity
}

export const useTprmSummaryLevelColumns = <T extends SupportedRow>({
  getViewRisksUrl,
  aggregationEntity,
}: Input<T>): ColumnConfig<T>[] => {
  const { TPRM } = useFeatureFlags()
  const riskSettings = useGetRiskSettings().data
  const riskMethodologyCalculator = RiskMethodologyCalculatorFactory(riskSettings?.riskScoreMethodology)
  const { getMaxRiskScoresInList } = useGetMaxRiskScoresInList()
  const { t } = useTranslation('requests')

  const checkFieldTypeGuard = <T extends SupportedRow>(
    field: Extract<keyof T, string> | 'inherentRisk' | 'residualRisk'
  ): field is 'inherentRisk' | 'residualRisk' => field === 'inherentRisk' || field === 'residualRisk'

  const getRiskMatrixScoreColumnConfig = ({
    field,
    headerName,
  }: {
    headerName: string
    field: Extract<keyof T, string>
  }): CustomRenderColumnConfig<T> => ({
    columnType: 'custom-render',
    field,
    headerName,
    valueGetter: (_, row) => {
      const maxRiskScore = getMaxRiskScoresInList(row.risks)
      if (!maxRiskScore) return null

      if (!checkFieldTypeGuard<T>(field)) return

      return riskMethodologyCalculator.getRiskScoreValue({
        score: maxRiskScore[field]?.score,
        probabilityScore: maxRiskScore[field]?.probability.score,
        impactScore: maxRiskScore[field]?.impact.score,
      })
    },
    renderCell: (params) => {
      if (!isDefined(params.value)) return null
      if (params.rowNode.type === 'group') {
        return <RowGroupingColumnTableCell columnHeaderName={headerName} value={params.value} id={params.id} />
      }

      const maxRiskScore = getMaxRiskScoresInList(params.row.risks)
      if (!maxRiskScore) return

      if (!checkFieldTypeGuard<T>(field)) return

      return (
        <RiskMatrixScoreChip
          probabilityId={maxRiskScore[field].probability.id}
          impactId={maxRiskScore[field].impact.id}
          showAggregationMessage
          showZeroScaleChip={field === 'residualRisk'}
          viewRisksLink={getViewRisksUrl(params)}
          aggregationEntity={aggregationEntity}
          size="small"
        />
      )
    },
  })

  return TPRM
    ? [
        getRiskMatrixScoreColumnConfig({
          headerName: t('table-column.tprm.inherent-risk-score.label'),
          field: 'inherentRisk' as Extract<keyof T, string>,
        }),
        getRiskMatrixScoreColumnConfig({
          headerName: t('table-column.tprm.residual-risk-score.label'),
          field: 'residualRisk' as Extract<keyof T, string>,
        }),
      ]
    : []
}
